import { Lightning } from "@lightningjs/sdk";
import { LiquidGlassShader } from "../shaders";
/** A cached glass panel, registered with the nearest ancestor GlassScene. */
export class GlassSurface extends Lightning.Component {
    static _template() {
        return {
            // Cache the shader's OUTPUT. A shader on this RTT element itself would
            // instead run on every draw of the cached input texture.
            renderToTexture: true,
            Material: {
                shader: { type: LiquidGlassShader },
            },
        };
    }
    _init() {
        // Request a result texture so Lightning retains this cache even when an
        // ancestor (such as Hero.Content) is also rendering to a texture.
        this.getTexture();
    }
    _attach() {
        this._unregister = this.fireAncestors("$registerGlassSurface", this);
    }
    _detach() {
        var _a;
        (_a = this._unregister) === null || _a === void 0 ? void 0 : _a.call(this);
        this._unregister = undefined;
    }
    set glassOptions(options) {
        this.tag("Material").patch({ shader: { options } });
    }
    set panelSize(size) {
        this._panelSize = size;
        // Resize the shader quad, not the backing render texture. Updating these
        // dimensions also schedules layout so GlassScene can sync its coordinates.
        this.tag("Material").patch({ w: size[0], h: size[1] });
    }
    /** Used by GlassScene; every surface receives the same TextureSource. */
    bindBackdrop(backdrop) {
        var _a;
        this._backdrop = backdrop;
        this._geometry = undefined;
        this.tag("Material").texture = (_a = backdrop === null || backdrop === void 0 ? void 0 : backdrop.getTexture()) !== null && _a !== void 0 ? _a : null;
    }
    /** Called after the scene's layout, before rendering, including list motion. */
    syncBackdrop() {
        var _a;
        const backdrop = this._backdrop;
        if (!backdrop)
            return;
        const w = backdrop.finalW;
        const h = backdrop.finalH;
        if (w <= 0 || h <= 0)
            return;
        const [ox, oy, xx, xy, , , yx, yy] = backdrop.core.getCornerPoints();
        const ax = (xx - ox) / w;
        const ay = (xy - oy) / w;
        const bx = (yx - ox) / h;
        const by = (yy - oy) / h;
        const determinant = ax * by - ay * bx;
        if (Math.abs(determinant) < 0.000001)
            return;
        const [sx, sy] = this.core.getCornerPoints();
        // Invert the backdrop's world transform. A common ancestor's scrolling,
        // scale or rotation cancels out, including nested render-to-texture nodes.
        const x = ((sx - ox) * by - (sy - oy) * bx) / determinant;
        const y = ((sy - oy) * ax - (sx - ox) * ay) / determinant;
        const [panelWidth, panelHeight] = (_a = this._panelSize) !== null && _a !== void 0 ? _a : [
            this.finalW,
            this.finalH,
        ];
        const geometry = [w, h, x, y, panelWidth, panelHeight];
        if (this._geometry &&
            geometry.every((value, i) => Math.abs(value - this._geometry[i]) < 0.0001)) {
            return;
        }
        this._geometry = geometry;
        this.tag("Material").patch({
            // Use numeric dimensions here. A result-texture update during rendering
            // makes Lightning temporarily replace function-based sizes with the
            // source's full dimensions, after layout has already finished.
            w: panelWidth,
            h: panelHeight,
            shader: { backdropSize: [w, h], backdropOffset: [x, y] },
        });
    }
}
export default GlassSurface;
//# sourceMappingURL=GlassSurface.js.map